import { useMemo, useState } from 'react';
import styles from '../../../styles/modules/Registration.module.css';
import { Step, StepLabel, Stepper } from '@mui/material';
import { ColorlibConnector, ColorlibStepIcon } from './RegistrationHelper';
import FormInfo from './FormInfo';
import FormWaiver from './FormWaiver';
import FormFeeDetails from './FormFeeDetails';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router';
import FormPaymentOptions from './FormPaymentOptions';
import { PaymentAPI } from '../../../api/PaymentAPI';
import Loader from '../../../component/Loader';
import { useDispatch } from 'react-redux';
import { rootAction } from '../../../redux/reducer/rootReducer';
import { EventAPI } from '../../../api/EventAPI';
import { checkoutVendorErrorMessage } from '../../../util/financialVendorGate';

const steps = [
  {
    label: 'Player Info'
  },
  {
    label: 'Participant Fee'
  },
  {
    label: 'Waiver'
  },
  {
    label: 'Payment'
  },
];


const Registration = () => {
  const selectedClub = useSelector((store) => store.user.selectedClub);
  const userInfo = useSelector((store) => store.user.userInfo);
  const [activeStep, setActiveStep] = useState(0);
  const [rosterRegistrationInfo, setRosterRegistrationInfo] = useState([]);
  const [formData, setFormData] = useState({});
  const [feeDetails, setFeeDetails] = useState([]);
  const [waiverDetails, setWaiverDetails] = useState([]);
  const [totalWaiver, setTotalWaiver] = useState(0);
  const [paymentMethod, setPaymentMethod] = useState({ id: '', name: '', desc: '', type: '' });
  const [isLoading, setIsLoading] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [yupObject, setYupObject] = useState({})
  const [childId, setChildId] = useState('')
  const { state } = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const handleCheckFieldError = async () => {
    try {
      await yupObject?.validate(formData, { abortEarly: false });
    } catch (error) {
      let errObj = {};
      error.inner.forEach(err => {
        errObj[err.path.split(".")[0]] = {
          ...errObj[err.path.split(".")[0]], [err.path.split(".")[1]]: err.message
        }
      })
      setFieldErrors(errObj);
      return Object.keys(errObj).length != 0;
    }
  };

  const handleNext = async () => {
    try{
      if (activeStep == 0) {
        const isError = await handleCheckFieldError();
        console.log('isError', isError);
        if (isError) {
          return;
        }
      }
      if (activeStep == 1 && feeDetails?.length == 0) {
        dispatch(rootAction.setWarningMessage({ id: Date.now(), type: 'warning', content: 'Please select at least one plan' }));
        return;
      }
      if (activeStep == 2 && totalWaiver !== waiverDetails.length) {
        dispatch(rootAction.setWarningMessage({ id: Date.now(), type: 'warning', content: 'Please accept all waiver' }));
        return;
      }
      if (activeStep != steps.length - 1) {
        await handleSubmitStepDetails('INIT');
        setActiveStep((prevActiveStep) => prevActiveStep + 1);
      }
    }catch(error){
      console.log(error);
    }
  };
  const handleSubmitStepDetails = async (status) => {
    try {
      let reqObj = {};
      if (activeStep == 0) {
        reqObj = {
          stepNumber: activeStep,
          stepName: steps[activeStep].label,
          data: formData
        }
      } else if (activeStep == 1) {
        reqObj = {
          stepNumber: activeStep,
          stepName: steps[activeStep].label,
          data: feeDetails
        }
      } else if (activeStep == 2) {
        reqObj = {
          stepNumber: activeStep,
          stepName: steps[activeStep].label,
          data: waiverDetails
        }
      } else if (activeStep == 3) {
        reqObj = {
          stepNumber: activeStep,
          stepName: steps[activeStep].label,
          data: paymentMethod
        }
      }
      const finalList = (() => {
        const findIndx = rosterRegistrationInfo.findIndex(item => item.stepNumber == reqObj.stepNumber);
        if (findIndx !== -1) {
          const updated = [...rosterRegistrationInfo];
          updated[findIndx] = { ...updated[findIndx], ...reqObj };
          return updated;
        } else {
          return [...rosterRegistrationInfo, reqObj];
        }
      })();
      const response = await EventAPI.postRegisterPlayerIntoEvent({
        eventId: state.eventId,
        clubId: selectedClub.clubId,
        isPlayer: state.isPlayer,
        playerRegistrationInfo: JSON.stringify(finalList),
        playerId: state.isPlayer ? state.playerId : null,
        childrenId: state.isPlayer ? null : state.child.childrenId ? state.child.childrenId : childId,
        guardianId: state.guardianId,
        templateId: state?.formTemplateId,
        childNotExist: state.child.childrenId ? false : true,
        status: status
      })
      setChildId(response.Data);
      setRosterRegistrationInfo(finalList);
    } catch (error) {
      console.log(error);
      dispatch(rootAction.setWarningMessage({ id: Date.now(), type: 'error', content: error?.response?.data ?? 'Please choose one payment method.' }));
      throw error;
    }
  };

  const handlePayment = async () => {
    try {
      if (!paymentMethod.id) {
        dispatch(rootAction.setWarningMessage({ id: Date.now(), type: 'warning', content: 'Please choose one payment method.' }));
        return;
      }

      const grossAmount = feeDetails.reduce((sum, curr) => sum + parseFloat(curr.feeAmount), 0);
      //console.log("Gross amount", grossAmount);
      if (grossAmount === 0) {
        dispatch(rootAction.setWarningMessage({ id: Date.now(), type: 'warning', content: 'Total amount is 0, please select offline or free payment method.' }));
        return;
      
      }

      const payReqObject = {
        amount: grossAmount,
        userId: state.playerId,
        clubId: selectedClub.clubId,
        eventId: state.eventId,
        isPlayer: state.isPlayer,
        childId: state.child.childrenId ? state.child.childrenId : childId,
        paymentMethodId: paymentMethod.id
      }
      setIsLoading(true)
      const response = await PaymentAPI.postCreatePaymentIntentForEvent(payReqObject);
      await handleSubmitStepDetails('INIT');
      navigate("/event-checkout", {
        replace:true,
        state: {
          clientSecret: response.Data.paymentIntentClientSecret,
          paymentIntentId: response.Data.paymentIntentId,
          totalAmount: response.Data.totalAmount,
          processingFee: response.Data.processingFee,
          feeDetails: feeDetails
        }
      });
    } catch (error) {
      const vendorMessage = checkoutVendorErrorMessage(error);
      if (vendorMessage) {
        dispatch(rootAction.setWarningMessage({
          id: Date.now(),
          type: 'error',
          content: vendorMessage,
        }));
      }
    } finally {
      setIsLoading(false);
    }
  };
  const handleOfflinePayment = async () => {
    try {

      const payReqObject = {
        amount: feeDetails.reduce((sum, curr) => sum + parseFloat(curr.feeAmount), 0),
        userId: state.playerId,
        clubId: selectedClub.clubId,
        eventId: state.eventId,
        isPlayer: state.isPlayer,
        childId: state.child.childrenId ? state.child.childrenId : childId,
        paymentMethodId: paymentMethod.id
      }
      setIsLoading(true)
      const response = await PaymentAPI.postOfflinePaymentForSeason(payReqObject);
      handleSubmitStepDetails('PENDING');
      dispatch(rootAction.setWarningMessage({ id: Date.now(), type: 'info', content: 'Registration request sent. Please complete your offline payment using the provided details.' }))
      navigate("/", { replace: true })
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  };
  const handleFreePayment = async() => {
    await handleSubmitStepDetails('APPROVED');
    dispatch(rootAction.setWarningMessage({ id: Date.now(), type: 'success', content: 'Registration complete! You’ve successfully registered for free.' }))
    navigate("/events", { replace:true })
  };
  const getComponentByIndex = (index) => {
    switch (index) {
      case 0: return <FormInfo isPlayer={state.isPlayer} setFieldErrors={setFieldErrors} fieldErrors={fieldErrors} setYupObject={setYupObject} child={state.child} formTemplateId={state?.formTemplateId} formData={formData} setFormData={setFormData} />
      case 1: return <FormFeeDetails feeDetails={feeDetails} setFeeDetails={setFeeDetails} formTemplateId={state?.formTemplateId} />
      case 2: return <FormWaiver setTotalWaiver={setTotalWaiver} waiverDetails={waiverDetails} setWaiverDetails={setWaiverDetails} formTemplateId={state?.formTemplateId} />
      case 3: return <FormPaymentOptions feeDetails={feeDetails} paymentMethod={paymentMethod} setPaymentMethod={setPaymentMethod} formTemplateId={state?.formTemplateId} />
    }
  };
  const handleBack = () => {
    if (activeStep != 0) {
      setActiveStep((prevActiveStep) => prevActiveStep - 1);
    }
  };
  const totalAmount = useMemo(() => {
    return feeDetails.reduce((sum, curr) => sum + parseFloat(curr.feeAmount || 0), 0);
  }, [feeDetails]);

  const isOffline = paymentMethod?.type === 'offline_payment';
  const isFree = totalAmount === 0;

  const handleClick = isFree
    ? handleFreePayment
    : isOffline
      ? handleOfflinePayment
      : handlePayment;

  const buttonLabel = (isOffline || isFree) ? 'Submit' : 'Pay';
  return (
    <div className={styles['registration_container']}>
      {isLoading && <Loader />}
      <div className={styles['registration_part1']}>
        <Stepper connector={<ColorlibConnector />} activeStep={activeStep} orientation="vertical">
          {steps.map((step, index) => (
            <Step key={index}>
              <StepLabel
                sx={{
                  padding: 0, '& .MuiStepLabel-label': {
                    fontSize: '1.1rem',
                  },
                }}
                StepIconComponent={ColorlibStepIcon}
              >
                {step.label}
              </StepLabel>
            </Step>
          ))}
        </Stepper>
      </div>
      <div className={styles['registration_part2']}>
        <div className={styles['registration_part2_sec_1']}>
          <button className={activeStep == 0 ? `${styles['form_reg_back_btn']} ${styles['disable']}` : styles['form_reg_back_btn']} type='button' onClick={handleBack}>Back</button>

          {(activeStep >= steps.length - 1) ?
            <button type='button' className={styles['form_reg_next_btn']} onClick={handleClick}>{buttonLabel}</button>
            :
            <button type='button' className={activeStep >= steps.length - 1 ? `${styles['form_reg_next_btn']} ${styles['disable']}` : styles['form_reg_next_btn']} onClick={handleNext}>Next</button>}
        </div>
        <div className={styles['registration_part2_sec_2']}>
          {
            getComponentByIndex(activeStep)
          }
        </div>
      </div>
    </div>
  )
}

export default Registration